import { Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useId, useState, type ComponentProps, type FormEvent, type ReactNode } from "react";
import { z } from "zod";
import type { AgentSettings, Autonomy, Channel, Effort, MachineTarget } from "../../shared/contracts";
import { DEFAULT_SETTINGS } from "../../shared/contracts";
import { LANGUAGE_OPTIONS, languageNameFr } from "../../shared/language";
import { isValidHm, isValidTimezone } from "../../shared/time";
import { IconCheck, IconChevronRight } from "../components/icons";
import { machineOptions } from "../components/mission-form";
import { DeleteAccount } from "../components/delete-account";
import { SnoozeControls } from "../components/snooze";
import { Button, cx, ErrorNotice, Field, Input, Loading, Notice, PageTop, RadioCard, RowList, Select, SELECT_ARROW, Switch } from "../components/ui";
import { useMachines, useSettings } from "../lib/agent";
import { api, errorText } from "../lib/api";
import { AUTONOMY_OPTIONS, EFFORT_OPTIONS, PRIMARY_CHANNEL_OPTIONS } from "../lib/format";
import { useNow, usePageTitle } from "../lib/hooks";
import { logout, setMe, useMe } from "../lib/session";
import { invalidate, mutateResource } from "../lib/store";
import { useDashboard } from "./pulse";

/** What this form edits: the agent's identity and way of writing live in « Ton agent » (/agent). */
type Editable = Omit<
  AgentSettings,
  | "snoozeUntil"
  | "onboarded"
  | "agentName"
  | "agentEmoji"
  | "persona"
  | "personaNotes"
  | "emojiLevel"
  | "messageLength"
  | "formality"
  | "agentGender"
  | "accentColor"
  | "avatar"
>;

/** Form state: numbers and lists are edited as text, validated on save. */
interface FormState {
  userName: string;
  timezone: string;
  language: string;
  primaryChannel: Channel;
  briefEnabled: boolean;
  briefTime: string;
  recapEnabled: boolean;
  recapTime: string;
  checkinMinutes: string;
  activeStart: string;
  activeEnd: string;
  weekends: boolean;
  briefWeekends: boolean;
  sweepHours: string;
  dailyProactiveCap: string;
  mutedTopics: string;
  quietStart: string;
  quietEnd: string;
  urgentBypassQuiet: boolean;
  autonomy: Autonomy;
  effortChat: Effort;
  defaultMachine: MachineTarget;
  computerModel: "auto" | "gpt" | "claude";
}

function toForm(raw: AgentSettings): FormState {
  // Older stored settings may predate newer fields: fill the gaps with the defaults.
  const s: AgentSettings = { ...DEFAULT_SETTINGS, ...raw };
  return {
    userName: s.userName,
    timezone: s.timezone,
    language: s.language,
    primaryChannel: s.primaryChannel,
    briefEnabled: s.briefEnabled,
    briefTime: s.briefTime,
    recapEnabled: s.recapEnabled,
    recapTime: s.recapTime,
    checkinMinutes: String(s.checkinMinutes),
    activeStart: s.activeStart,
    activeEnd: s.activeEnd,
    weekends: s.weekends,
    briefWeekends: s.briefWeekends,
    sweepHours: String(s.sweepHours),
    dailyProactiveCap: s.dailyProactiveCap === null ? "" : String(s.dailyProactiveCap),
    mutedTopics: s.mutedTopics.join(", "),
    quietStart: s.quietStart,
    quietEnd: s.quietEnd,
    urgentBypassQuiet: s.urgentBypassQuiet,
    autonomy: s.autonomy,
    effortChat: s.effortChat,
    defaultMachine: s.defaultMachine,
    computerModel: s.computerModel === "gpt" || s.computerModel === "claude" ? s.computerModel : "auto",
  };
}

const hm = (msg: string) => z.string().trim().refine(isValidHm, msg);
const int = (min: number, max: number, msg: string) => z.coerce.number({ error: msg }).int(msg).min(min, msg).max(max, msg);

const schema = z.object({
  userName: z.string().trim().min(1, "Indique ton prénom.").max(60, "60 caractères maximum."),
  timezone: z.string().trim().refine(isValidTimezone, "Fuseau horaire inconnu (ex. Europe/Paris)."),
  language: z.string().regex(/^[a-z]{2}$/, "Langue inconnue."),
  primaryChannel: z.enum(["web", "imessage", "sms", "telegram", "email"]),
  briefEnabled: z.boolean(),
  briefTime: hm("Heure invalide (HH:MM)."),
  recapEnabled: z.boolean(),
  recapTime: hm("Heure invalide (HH:MM)."),
  checkinMinutes: int(15, 240, "Entre 15 et 240 minutes."),
  activeStart: hm("Heure invalide (HH:MM)."),
  activeEnd: hm("Heure invalide (HH:MM)."),
  weekends: z.boolean(),
  briefWeekends: z.boolean(),
  sweepHours: int(0, 12, "Entre 0 (désactivée) et 12 heures."),
  // Empty = no daily cap (the default): the agent learns from the owner's reactions what deserves a message.
  dailyProactiveCap: z.union([z.string().trim().length(0).transform(() => null), int(0, 20, "Vide (pas de plafond) ou entre 0 et 20 messages.")]),
  // The agent keeps at most 20 topics of 60 characters (agent/settings.ts).
  mutedTopics: z
    .string()
    .transform((v) => [...new Set(v.split(/[,\n]/).map((t) => t.trim()).filter(Boolean))])
    .pipe(z.array(z.string().max(60, "Un sujet fait 60 caractères maximum.")).max(20, "20 sujets maximum.")),
  quietStart: hm("Heure invalide (HH:MM)."),
  quietEnd: hm("Heure invalide (HH:MM)."),
  urgentBypassQuiet: z.boolean(),
  autonomy: z.enum(["prudent", "equilibre", "autonome"]),
  effortChat: z.enum(["low", "medium", "high"]),
  defaultMachine: z.custom<MachineTarget>((v) => typeof v === "string" && (v === "none" || v === "cloud" || /^local:[A-Za-z0-9_-]{1,64}$/.test(v)), "Machine inconnue."),
  computerModel: z.enum(["auto", "gpt", "claude"]),
});

type Errors = Partial<Record<keyof FormState, string>>;

/** Only the fields that changed (PATCH semantics). */
function diff(before: AgentSettings, after: Editable): Partial<Editable> {
  const out: Partial<Record<keyof Editable, unknown>> = {};
  for (const key of Object.keys(after) as (keyof Editable)[]) {
    if (JSON.stringify(after[key]) !== JSON.stringify(before[key])) out[key] = after[key];
  }
  return out as Partial<Editable>;
}

/** /reglages — the rhythm, how much it talks, appearance and language, the account; the fine-tuning folded away. */
export function SettingsPage() {
  usePageTitle("Réglages");
  const settings = useSettings();
  const now = useNow(30_000);

  if (settings.loading) return <Loading />;
  if (!settings.data) return <ErrorNotice message={settings.error?.message ?? "Impossible de charger tes réglages."} onRetry={() => void settings.reload()} />;

  return (
    <div className="mx-auto max-w-[620px]">
      <PageTop title="Réglages" />
      <SettingsForm settings={settings.data} now={now} />
    </div>
  );
}

/** A row of a panel (V2Reglages): the label at 15 px, the controls on the right (wrapped under it on a phone). */
function Row({ title, line, htmlFor, children }: { title: string; line?: ReactNode; htmlFor?: string; children?: ReactNode }) {
  const Title = htmlFor ? "label" : "span";
  return (
    <div className="flex min-h-[54px] flex-wrap items-center gap-x-3 gap-y-2 py-[9px]">
      <span className="min-w-0 flex-[1_1_96px]">
        <Title {...(htmlFor ? { htmlFor } : {})} className="block text-[15px]">
          {title}
        </Title>
        {line ? <span className="block text-[12.5px] text-subtle">{line}</span> : null}
      </span>
      {children ? <span className="flex flex-wrap items-center gap-2">{children}</span> : null}
    </div>
  );
}

/** A section of the page: its faint 13 px label, then its panel. */
function SettingsSection({ id, title, children }: { id: string; title: string; children: ReactNode }) {
  return (
    <section id={id} aria-labelledby={`${id}-t`} className="flex scroll-mt-24 flex-col gap-2">
      <h2 id={`${id}-t`} className="px-1 text-[13px] font-semibold text-subtle">
        {title}
      </h2>
      {children}
    </section>
  );
}

/** A compact field on a chip: a time, a first name, a time zone. */
const SMALL_FIELD =
  "h-9 rounded-full bg-chip px-3.5 text-[14px] text-fg outline-none transition focus-visible:shadow-[inset_0_0_0_1.5px_var(--fg-subtle)] max-md:h-11 [&::-webkit-calendar-picker-indicator]:opacity-50";
const SMALL_SELECT =
  "h-9 w-auto min-w-36 max-w-full appearance-none rounded-full bg-chip bg-[length:16px] bg-[right_10px_center] bg-no-repeat pr-9 pl-3.5 text-[14px] text-fg outline-none transition focus-visible:shadow-[inset_0_0_0_1.5px_var(--fg-subtle)] max-md:h-11";

/** A select on a chip, like the compact fields. */
function ChipSelect(props: ComponentProps<"select">) {
  return <select {...props} className={SMALL_SELECT} style={{ backgroundImage: SELECT_ARROW }} />;
}

/** « Combien il te parle »: three presets over the daily cap and the check-in pace. */
type Pace = "discret" | "apprend" | "present";
const PACES: { value: Pace; label: string }[] = [
  { value: "discret", label: "Discret" },
  { value: "apprend", label: "Il apprend" },
  { value: "present", label: "Présent" },
];
function paceOf(f: { dailyProactiveCap: string; checkinMinutes: string }): Pace | null {
  const cap = f.dailyProactiveCap.trim() === "" ? null : Number(f.dailyProactiveCap);
  if (cap !== null && cap <= 2) return "discret";
  if (cap === null && Number(f.checkinMinutes) <= 30) return "present";
  if (cap === null) return "apprend";
  return null;
}

function SettingsForm({ settings, now }: { settings: AgentSettings; now: number }) {
  const dash = useDashboard();
  const navigate = useNavigate();
  const [loggingOut, setLoggingOut] = useState(false);
  const me = useMe();
  const machines = useMachines();
  const [form, setForm] = useState<FormState>(() => toForm(settings));
  const [dirty, setDirty] = useState(false);
  const [errors, setErrors] = useState<Errors>({});
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);
  const p = useId();
  const id = (name: string) => `${p}-${name}`;

  // Updates pushed from elsewhere (another tab, the agent itself) refresh the form until the user starts editing.
  useEffect(() => {
    if (!dirty) setForm(toForm(settings));
  }, [settings, dirty]);

  function set<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((f) => ({ ...f, [key]: value }));
    setDirty(true);
    setSaved(false);
    setErrors((e) => (e[key] ? { ...e, [key]: undefined } : e));
  }

  async function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (busy) return;
    const parsed = schema.safeParse(form);
    if (!parsed.success) {
      const next: Errors = {};
      for (const issue of parsed.error.issues) {
        const key = issue.path[0] as keyof FormState | undefined;
        if (key && !next[key]) next[key] = issue.message;
      }
      setErrors(next);
      setError("Vérifie les champs en rouge.");
      return;
    }
    setBusy(true);
    setError(null);
    setSaved(false);
    try {
      const { timezone, ...rest } = parsed.data;
      const patch = diff(settings, rest as Editable);
      let next = settings;
      if (Object.keys(patch).length) next = await api("PATCH /api/settings", { body: patch });
      if (timezone !== settings.timezone) {
        // The account's time zone drives every schedule: it is changed through /api/me, which updates the agent too.
        setMe(await api("PATCH /api/me", { body: { timezone } }));
        next = await api("GET /api/settings");
      }
      mutateResource<AgentSettings>("settings", () => next);
      invalidate("dashboard");
      setForm(toForm(next));
      setDirty(false);
      setErrors({});
      setSaved(true);
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  // The six languages offered, plus the one the agent may have set from the conversation.
  const languageChoices = LANGUAGE_OPTIONS.some((o) => o.code === form.language)
    ? LANGUAGE_OPTIONS
    : [...LANGUAGE_OPTIONS, { code: form.language, label: languageNameFr(form.language) }];
  const machineChoices = machineOptions(machines.data);
  if (!machineChoices.some((o) => o.value === form.defaultMachine)) machineChoices.push({ value: form.defaultMachine, label: "Machine retirée" });

  const pace = paceOf(form);
  const usage = dash.data?.usage;
  const pct = usage && usage.userBudgetUsd > 0 ? Math.min(100, Math.round((usage.monthUsd / usage.userBudgetUsd) * 100)) : null;
  const renew = new Date(now);
  renew.setMonth(renew.getMonth() + 1, 1);
  const agentName = settings.agentName;

  return (
    <div className="flex flex-col gap-[26px] pb-6">
    <form onSubmit={submit} noValidate className="flex flex-col gap-[26px]">
      <SettingsSection id="rythme" title="Ton rythme">
        <RowList>
          <Row title="Brief du matin">
            <label htmlFor={id("briefTime")} className="sr-only">
              Heure du brief
            </label>
            <input id={id("briefTime")} type="time" value={form.briefTime} onChange={(e) => set("briefTime", e.target.value)} className={SMALL_FIELD} />
            <input id={id("briefEnabled")} type="checkbox" className="switch" aria-label="Brief du matin" checked={form.briefEnabled} onChange={(e) => set("briefEnabled", e.target.checked)} />
          </Row>
          <Row title="Récap du soir">
            <label htmlFor={id("recapTime")} className="sr-only">
              Heure du récap
            </label>
            <input id={id("recapTime")} type="time" value={form.recapTime} onChange={(e) => set("recapTime", e.target.value)} className={SMALL_FIELD} />
            <input id={id("recapEnabled")} type="checkbox" className="switch" aria-label="Récap du soir" checked={form.recapEnabled} onChange={(e) => set("recapEnabled", e.target.checked)} />
          </Row>
          <Row title="Heures calmes">
            <label htmlFor={id("quietStart")} className="sr-only">
              Début des heures calmes
            </label>
            <input id={id("quietStart")} type="time" value={form.quietStart} onChange={(e) => set("quietStart", e.target.value)} className={SMALL_FIELD} />
            <span aria-hidden="true" className="text-subtle">
              →
            </span>
            <label htmlFor={id("quietEnd")} className="sr-only">
              Fin des heures calmes
            </label>
            <input id={id("quietEnd")} type="time" value={form.quietEnd} onChange={(e) => set("quietEnd", e.target.value)} className={SMALL_FIELD} />
          </Row>
          <Row title="L'urgent passe quand même" htmlFor={id("urgentBypassQuiet")}>
            <input
              id={id("urgentBypassQuiet")}
              type="checkbox"
              className="switch"
              aria-label="Laisser passer l'urgent"
              checked={form.urgentBypassQuiet}
              onChange={(e) => set("urgentBypassQuiet", e.target.checked)}
            />
          </Row>
          <Row title="Pause">
            <SnoozeControls settings={settings} now={now} />
          </Row>
        </RowList>
        {errors.briefTime || errors.recapTime || errors.quietStart || errors.quietEnd ? (
          <p className="text-[12.5px] text-danger" role="alert">
            {errors.briefTime ?? errors.recapTime ?? errors.quietStart ?? errors.quietEnd}
          </p>
        ) : null}
      </SettingsSection>

      <SettingsSection id="frequence" title="Combien il te parle">
        <fieldset className="grid grid-cols-3 gap-1 rounded-[18px] bg-elev p-1">
          <legend className="sr-only">Combien il te parle</legend>
          {PACES.map((o) => (
            <label
              key={o.value}
              className={cx(
                "flex h-10 min-w-0 cursor-pointer items-center justify-center rounded-[14px] px-2 text-center text-[14.5px] font-semibold transition-colors has-[:focus-visible]:shadow-[inset_0_0_0_1.5px_var(--fg-subtle)] max-md:h-11",
                pace === o.value ? "bg-bubble-user text-fg" : "text-muted hover:text-fg",
              )}
            >
              <input
                type="radio"
                name="pace"
                value={o.value}
                className="sr-only"
                checked={pace === o.value}
                onChange={() => {
                  if (o.value === "discret") {
                    set("dailyProactiveCap", "2");
                  } else if (o.value === "apprend") {
                    set("dailyProactiveCap", "");
                    if (Number(form.checkinMinutes) <= 30) set("checkinMinutes", String(DEFAULT_SETTINGS.checkinMinutes));
                  } else {
                    set("dailyProactiveCap", "");
                    set("checkinMinutes", "30");
                  }
                }}
              />
              <span className="truncate">{o.label}</span>
            </label>
          ))}
        </fieldset>
      </SettingsSection>

      <SettingsSection id="apparence" title="Langue et toi">
        <RowList>
          <Row title={`Langue de ${agentName}`} htmlFor={id("language")}>
            <ChipSelect id={id("language")} value={form.language} onChange={(e) => set("language", e.target.value)}>
              {languageChoices.map((o) => (
                <option key={o.code} value={o.code}>
                  {o.label}
                </option>
              ))}
            </ChipSelect>
          </Row>
          <Row title="Ton prénom" htmlFor={id("userName")}>
            <input id={id("userName")} value={form.userName} maxLength={60} autoComplete="given-name" onChange={(e) => set("userName", e.target.value)} className={cx(SMALL_FIELD, "w-40")} />
          </Row>
          <Row title="Fuseau horaire" htmlFor={id("timezone")}>
            <input id={id("timezone")} value={form.timezone} maxLength={64} spellCheck={false} onChange={(e) => set("timezone", e.target.value)} className={cx(SMALL_FIELD, "w-40")} />
          </Row>
          <Row title="Où il t'écrit d'abord" htmlFor={id("primaryChannel")}>
            <ChipSelect id={id("primaryChannel")} value={form.primaryChannel} onChange={(e) => set("primaryChannel", e.target.value as Channel)}>
              {PRIMARY_CHANNEL_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </ChipSelect>
          </Row>
        </RowList>
        {errors.userName || errors.timezone || errors.language ? (
          <p className="text-[12.5px] text-danger" role="alert">
            {errors.userName ?? errors.timezone ?? errors.language}
          </p>
        ) : null}
      </SettingsSection>

      <details className="group">
        <summary className="inline-flex min-h-11 cursor-pointer list-none items-center gap-1.5 px-1 text-[13px] font-semibold text-subtle select-none hover:text-muted [&::-webkit-details-marker]:hidden">
          <span>Réglages fins</span>
          <IconChevronRight width={12} height={12} strokeWidth={2.6} aria-hidden="true" className="transition group-open:rotate-90" />
        </summary>
        <div className="mt-2 flex flex-col gap-5 rounded-[20px] bg-elev p-4 sm:p-5">
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Check-in toutes les (minutes)" htmlFor={id("checkinMinutes")} error={errors.checkinMinutes} hint="Entre 15 et 240.">
              <Input id={id("checkinMinutes")} type="number" inputMode="numeric" min={15} max={240} value={form.checkinMinutes} onChange={(e) => set("checkinMinutes", e.target.value)} />
            </Field>
            <Field label="Plafond par jour (facultatif)" htmlFor={id("dailyProactiveCap")} error={errors.dailyProactiveCap} hint="Vide : il apprend de tes réactions.">
              <Input id={id("dailyProactiveCap")} type="number" inputMode="numeric" min={0} max={20} placeholder="Aucun" value={form.dailyProactiveCap} onChange={(e) => set("dailyProactiveCap", e.target.value)} />
            </Field>
            <Field label="Début de journée" htmlFor={id("activeStart")} error={errors.activeStart}>
              <Input id={id("activeStart")} type="time" value={form.activeStart} onChange={(e) => set("activeStart", e.target.value)} />
            </Field>
            <Field label="Fin de journée" htmlFor={id("activeEnd")} error={errors.activeEnd}>
              <Input id={id("activeEnd")} type="time" value={form.activeEnd} onChange={(e) => set("activeEnd", e.target.value)} />
            </Field>
            <Field label="Veille des apps toutes les (heures)" htmlFor={id("sweepHours")} error={errors.sweepHours} hint="0 = désactivée, jusqu'à 12.">
              <Input id={id("sweepHours")} type="number" inputMode="numeric" min={0} max={12} value={form.sweepHours} onChange={(e) => set("sweepHours", e.target.value)} />
            </Field>
            <Field label="Sujets à ne pas aborder" htmlFor={id("mutedTopics")} error={errors.mutedTopics} hint="Ex. foot, promotions">
              <Input id={id("mutedTopics")} value={form.mutedTopics} maxLength={2000} onChange={(e) => set("mutedTopics", e.target.value)} />
            </Field>
          </div>
          <div className="-my-2 divide-y divide-sunken">
            <Switch id={id("briefWeekends")} label="Brief et récap le week-end" checked={form.briefWeekends} onChange={(v) => set("briefWeekends", v)} />
            <Switch id={id("weekends")} label="Check-ins le week-end" checked={form.weekends} onChange={(v) => set("weekends", v)} />
          </div>
          <fieldset>
            <legend className="mb-2 text-[13px] font-medium">Autonomie</legend>
            <div className="grid gap-2">
              {AUTONOMY_OPTIONS.map((o) => (
                <RadioCard key={o.value} name="autonomy" value={o.value} label={o.label} description={o.description} checked={form.autonomy === o.value} onChange={() => set("autonomy", o.value)} />
              ))}
            </div>
          </fieldset>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Effort de réflexion en discussion" htmlFor={id("effortChat")}>
              <Select id={id("effortChat")} value={form.effortChat} onChange={(e) => set("effortChat", e.target.value as Effort)}>
                {EFFORT_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Machine des missions" htmlFor={id("defaultMachine")} error={errors.defaultMachine}>
              <Select id={id("defaultMachine")} value={form.defaultMachine} onChange={(e) => set("defaultMachine", e.target.value as MachineTarget)}>
                {machineChoices.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Modèle pour piloter l'ordinateur" htmlFor={id("computerModel")} error={errors.computerModel}>
              <Select id={id("computerModel")} value={form.computerModel} onChange={(e) => set("computerModel", e.target.value as FormState["computerModel"])}>
                <option value="auto">Automatique (recommandé)</option>
                <option value="gpt">GPT-6.1 Terra rapide</option>
                <option value="claude">Claude Opus 5.5</option>
              </Select>
            </Field>
          </div>
        </div>
      </details>

      <div className={cx("flex flex-wrap items-center gap-3", dirty && "sticky bottom-3 z-10 -mx-1 rounded-full bg-bg/95 p-1 md:bottom-4")}>
        <Button type="submit" busy={busy}>
          Enregistrer
        </Button>
        {saved ? (
          <span className="cv-pop inline-flex items-center gap-1.5 text-[13px] text-muted" role="status">
            <span aria-hidden="true" className="flex h-[18px] w-[18px] items-center justify-center rounded-full bg-ink text-ink-fg">
              <IconCheck width={10} height={10} strokeWidth={3.6} />
            </span>
            Réglages enregistrés
          </span>
        ) : dirty ? (
          <span className="text-[12.5px] text-subtle">Pas encore enregistré</span>
        ) : null}
        {error ? (
          <Notice tone="danger" role="alert" className="w-full">
            {error}
          </Notice>
        ) : null}
      </div>
    </form>

      <SettingsSection id="compte" title="Ton compte">
        <RowList>
          <div className="flex min-h-[46px] flex-wrap items-center gap-x-3 gap-y-1 py-[11px] text-[15px]">
            <span className="min-w-0 flex-1 truncate">{me?.email || me?.phone || me?.name || "Ta session"}</span>
            {pct !== null ? (
              <span className="text-[13px] text-subtle" role="meter" aria-label="Budget du mois" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100} title={`Se renouvelle le 1er ${new Intl.DateTimeFormat("fr-FR", { month: "long" }).format(renew)}`}>
                Budget du mois · {pct} %
              </span>
            ) : null}
          </div>
          <LinkRow to="/activite" title="Activité" />
          <LinkRow to="/agents" title="Mes agents" />
          <button
            type="button"
            disabled={loggingOut}
            aria-busy={loggingOut || undefined}
            onClick={async () => {
              setLoggingOut(true);
              await logout();
              setLoggingOut(false);
              void navigate({ to: "/connexion", replace: true });
            }}
            className="flex min-h-11 w-full items-center py-[11px] text-left text-[15px] text-fg transition hover:text-muted disabled:opacity-60"
          >
            Se déconnecter
          </button>
          <DeleteAccount />
        </RowList>
      </SettingsSection>
    </div>
  );
}

function LinkRow({ to, title }: { to: "/activite" | "/agents"; title: string }) {
  return (
    <Link to={to} className="flex min-h-11 items-center gap-3 py-[11px] text-[15px] transition hover:text-muted">
      <span className="min-w-0 flex-1">{title}</span>
      <IconChevronRight width={14} height={14} className="shrink-0 text-subtle" />
    </Link>
  );
}
